// client/src/components/catalogoModelo/CatalogoModeloPage.js
import React, { Fragment, useState, useCallback, useMemo } from 'react';
import { Package, PlusCircle, Upload, Sparkles, Loader2, MoreVertical, Edit, Trash2, XCircle } from 'lucide-react';
import PageHeader from '../layout/PageHeader';
import FabButton from '../common/FabButton';
import ActionsDrawer from '../common/ActionsDrawer';
import CatalogoModeloForm from './CatalogoModeloForm';
import ImportarCsvCatalogoModeloForm from './ImportarCsvCatalogoModeloForm';
import GerarCatalogoIAForm from './GerarCatalogoIAForm';
import { useCatalogoModeloItems, useMutateCatalogoModelo } from '../../hooks/useCatalogoModelo';

// ✅ Administração do Catálogo de Modelos — só Matriz gerencia (ver
// server/routes/catalogoModeloRoutes.js, authorize('matriz') nas rotas de
// escrita). Conta/Franquia só navegam a lista pública em
// ImportarCatalogoPage.js e importam pra própria loja.
const CatalogoModeloPage = () => {
    const [viewState, setViewState] = useState({ showList: true, showForm: false, showImport: false, showGerarIA: false });
    const [current, setCurrent] = useState(null);
    const [selectedItem, setSelectedItem] = useState(null);
    const [filtroTipo, setFiltroTipo] = useState('');

    const queryParams = useMemo(() => (filtroTipo ? { tipo: filtroTipo } : {}), [filtroTipo]);
    const { data, isLoading } = useCatalogoModeloItems(queryParams);
    const { deleteItem } = useMutateCatalogoModelo();

    const itens = data?.itens || [];

    const handleAddNew = useCallback(() => {
        setCurrent(null);
        setViewState({ showList: false, showForm: true, showImport: false, showGerarIA: false });
    }, []);

    const handleImportarCsvClick = useCallback(() => {
        setViewState({ showList: false, showForm: false, showImport: true, showGerarIA: false });
    }, []);

    const handleGerarIAClick = useCallback(() => {
        setViewState({ showList: false, showForm: false, showImport: false, showGerarIA: true });
    }, []);

    const handleEditar = useCallback((item) => {
        setCurrent(item);
        setViewState({ showList: false, showForm: true, showImport: false, showGerarIA: false });
        setSelectedItem(null);
    }, []);

    const handleRemover = useCallback((id) => {
        if (window.confirm('Deseja realmente remover este item do catálogo?')) {
            deleteItem.mutate(id, { onSuccess: () => setSelectedItem(null) });
        }
    }, [deleteItem]);

    const handleClose = useCallback(() => {
        setViewState({ showList: true, showForm: false, showImport: false, showGerarIA: false });
        setCurrent(null);
    }, []);

    const isViewingPanel = viewState.showForm || viewState.showImport || viewState.showGerarIA;

    return (
        <Fragment>
            <div className="page-management-container">
                <PageHeader
                    icon={<Package />}
                    title="Catálogo de Modelos"
                    subtitle={!isLoading ? `${itens.length} item${itens.length !== 1 ? 's' : ''}` : null}
                    actions={<>
                        {!isViewingPanel && (
                            <>
                                <button onClick={handleGerarIAClick} className="action-button action-button-secondary">
                                    <Sparkles size={18} className="icon" /> Gerar Catálogo com IA
                                </button>
                                <button onClick={handleImportarCsvClick} className="action-button action-button-secondary">
                                    <Upload size={18} className="icon" /> Importar CSV
                                </button>
                            </>
                        )}
                        {isViewingPanel && (
                            <button onClick={handleClose} className="action-button action-button-secondary">
                                <XCircle size={18} className="icon" /> Fechar
                            </button>
                        )}
                    </>}
                />

                {viewState.showForm && (
                    <div className="content-panel">
                        <CatalogoModeloForm current={current} onCloseForm={handleClose} />
                    </div>
                )}

                {viewState.showImport && (
                    <div className="content-panel">
                        <ImportarCsvCatalogoModeloForm onClose={handleClose} />
                    </div>
                )}

                {viewState.showGerarIA && (
                    <div className="content-panel">
                        <GerarCatalogoIAForm onClose={handleClose} />
                    </div>
                )}

                {viewState.showList && (
                    <div className="content-panel">
                        <div className="form-group" style={{ maxWidth: 260 }}>
                            <label className="form-label">Filtrar por Tipo</label>
                            <select value={filtroTipo} onChange={(e) => setFiltroTipo(e.target.value)} className="form-input">
                                <option value="">Todos</option>
                                <option value="servico">Serviço</option>
                                <option value="produto">Produto</option>
                            </select>
                        </div>
                    </div>
                )}
            </div>

            {viewState.showList && (
                <div className="table-container">
                    <div className="table-wrapper">
                        {isLoading ? (
                            <div className="table-loading-state">
                                <Loader2 size={24} className="animate-spin" />
                                <span>Carregando catálogo...</span>
                            </div>
                        ) : itens.length > 0 ? (
                            <table className="styled-table">
                                <thead>
                                    <tr>
                                        <th style={{ textAlign: 'left' }}>Nome</th>
                                        <th>Categoria</th>
                                        <th>Segmento</th>
                                        <th>Tipo</th>
                                        <th>Preço Sugerido</th>
                                        <th>Status</th>
                                        <th className="text-right">Ações</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {itens.map((item) => (
                                        <tr key={item._id} className="hover:bg-gray-50">
                                            <td className="py-3">{item.nome}</td>
                                            <td className="py-3">{item.categoriaNome}</td>
                                            <td className="py-3">{item.segmento?.nome || '—'}</td>
                                            <td className="py-3">
                                                <span className={`badge ${item.tipo === 'servico' ? 'badge-blue' : 'badge-purple'}`}>
                                                    {item.tipo === 'servico' ? 'Serviço' : 'Produto'}
                                                </span>
                                            </td>
                                            <td className="py-3">
                                                {item.precoSugerido !== undefined && item.precoSugerido !== null
                                                    ? Number(item.precoSugerido).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
                                                    : '—'}
                                            </td>
                                            <td className="py-3">
                                                <span className={`badge ${item.ativo ? 'badge-green' : 'badge-gray'}`}>
                                                    {item.ativo ? 'Ativo' : 'Inativo'}
                                                </span>
                                            </td>
                                            <td className="text-right">
                                                <button
                                                    onClick={() => setSelectedItem(item)}
                                                    title="Ações"
                                                    className="action-button-icon text-gray-600 hover:text-gray-900"
                                                >
                                                    <MoreVertical size={18} />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : (
                            <div className="table-empty-state">
                                <Package size={48} />
                                <h3>Nenhum item no catálogo</h3>
                                <p>Clique em "Adicionar Item" ou "Importar CSV" para começar.</p>
                            </div>
                        )}
                    </div>
                </div>
            )}

            <FabButton onClick={handleAddNew} visible={!isViewingPanel} />

            <ActionsDrawer
                open={!!selectedItem}
                onClose={() => setSelectedItem(null)}
                title={selectedItem?.nome || 'Item do Catálogo'}
                actions={selectedItem ? [
                    {
                        key: 'editar',
                        label: 'Editar',
                        icon: <Edit size={20} className="icon" />,
                        onClick: () => handleEditar(selectedItem),
                    },
                    {
                        key: 'excluir',
                        label: 'Excluir',
                        icon: <Trash2 size={20} className="icon" />,
                        onClick: () => handleRemover(selectedItem._id),
                        destructive: true,
                        loading: deleteItem.isPending,
                    },
                ] : []}
            />
        </Fragment>
    );
};

export default CatalogoModeloPage;
